import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { useSupplier } from "../../context/SupplierContext";
import HubModeNotice from "../../components/HubModeNotice";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, MessageSquare, Check, Clock } from "lucide-react";
import { toast } from "sonner";

export default function BuyerRfq() {
  const { activeSellerId, activeSellerName } = useSupplier();
  const [rfqs, setRfqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", target_qty: 100, target_price: 0 });
  const [active, setActive] = useState(null);

  const load = () => api.get("/rfq").then(r => setRfqs(r.data));
  useEffect(() => { load(); }, []);

  // Scope to active supplier workspace. When a buyer picks a specific supplier,
  // only show RFQs addressed to that supplier (and hide other-supplier quotes).
  const scopedRfqs = useMemo(() => {
    if (!activeSellerId) return rfqs;
    return rfqs
      .filter(r => Array.isArray(r.seller_ids) && r.seller_ids.includes(activeSellerId))
      .map(r => ({
        ...r,
        quotes: (r.quotes || []).filter(q => q.seller_id === activeSellerId),
      }));
  }, [rfqs, activeSellerId]);

  const hiddenOtherSupplierCount = activeSellerId
    ? rfqs.length - scopedRfqs.length
    : 0;

  const submit = async () => {
    if (!f.title) return toast.error("Title is required");
    const payload = {
      ...f,
      target_qty: Number(f.target_qty),
      target_price: Number(f.target_price) || null,
    };
    if (activeSellerId) payload.seller_ids = [activeSellerId];
    await api.post("/rfq", payload);
    toast.success(activeSellerId
      ? `RFQ sent to ${activeSellerName} — quote will arrive within 48 hours`
      : "RFQ submitted — quotes will arrive within 48 hours");
    setOpen(false); setF({ title: "", description: "", target_qty: 100, target_price: 0 });
    load();
  };

  const accept = async (rfq, sellerId) => {
    await api.post(`/rfq/${rfq.id}/accept/${sellerId}`);
    toast.success("Quote accepted — order draft created");
    load();
    setActive(null);
  };

  return (
    <>
      <PageHeader title="Request for Quote" subtitle={activeSellerId ? `Negotiate custom pricing & MOQs with ${activeSellerName}` : "Negotiate custom pricing & MOQs across suppliers"}
        action={<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-rfq-btn" className="rounded-full bg-foreground text-background hover:bg-foreground/90"><Plus className="w-4 h-4 mr-1"/>New RFQ</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit RFQ</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {activeSellerId && (
                <div className="rounded-lg bg-secondary/50 border border-border px-3 py-2 text-[11px] text-foreground/70" data-testid="rfq-scope-notice">
                  This RFQ will be sent only to <span className="font-medium text-foreground">{activeSellerName}</span> · switch to the hub to broadcast to all suppliers.
                </div>
              )}
              <div><Label>Title</Label><Input value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Need 500 units of premium serum" data-testid="rfq-title"/></div>
              <div><Label>Description</Label><textarea value={f.description} onChange={e=>setF({...f,description:e.target.value})} placeholder="Describe specs, packaging, delivery needs..." className="w-full rounded-md border border-input bg-white p-3 text-sm h-24" data-testid="rfq-desc"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Target qty</Label><Input type="number" value={f.target_qty} onChange={e=>setF({...f,target_qty:e.target.value})} data-testid="rfq-qty"/></div>
                <div><Label>Target unit price (optional)</Label><Input type="number" value={f.target_price} onChange={e=>setF({...f,target_price:e.target.value})} placeholder="$"/></div>
              </div>
              <Button onClick={submit} data-testid="rfq-submit" className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90">Send to suppliers</Button>
              <p className="text-[10px] text-center text-foreground/50">{activeSellerId ? `Will be sent to ${activeSellerName} only · auto-quote enabled` : "Will be sent to all active suppliers · auto-quote enabled"}</p>
            </div>
          </DialogContent>
        </Dialog>}/>
      <div className="p-8 space-y-5">
        {!activeSellerId && (
          <HubModeNotice
            page="RFQ workflow"
            description="RFQs go to verqo-connected suppliers in real time. While you connect your first supplier, you can still send branded POs to any supplier — even ones not on verqo."
          />
        )}
        {hiddenOtherSupplierCount > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-2.5 text-[11px] text-amber-900 inline-flex items-center gap-2" data-testid="rfq-hidden-notice">
            <MessageSquare className="w-3.5 h-3.5"/>
            {hiddenOtherSupplierCount} RFQ{hiddenOtherSupplierCount !== 1 ? "s" : ""} hidden — not addressed to {activeSellerName}. Switch to the hub to see all.
          </div>
        )}
        {scopedRfqs.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border p-16 text-center">
            <MessageSquare className="w-10 h-10 mx-auto text-foreground/30 mb-3"/>
            <div className="display text-lg">{activeSellerId ? `No RFQs with ${activeSellerName} yet` : "No RFQs yet"}</div>
            <div className="text-sm text-foreground/60 mt-1">Submit a request to receive competing quotes from suppliers.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {scopedRfqs.map(r => (
              <div key={r.id} className="rounded-2xl bg-white border border-border p-6" data-testid={`rfq-${r.id.slice(0,6)}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="display text-lg">{r.title}</div>
                    <div className="text-sm text-foreground/60 mt-1 line-clamp-2">{r.description}</div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground/60">
                      <span>{r.target_qty} units</span>
                      {r.target_price && <span>· target {money(r.target_price)}/unit</span>}
                      <span>· sent to {r.seller_ids.length} suppliers</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {r.status === "awarded" ? <span className="chip bg-accent/30 border border-accent/40"><Check className="w-3 h-3"/>Awarded</span> :
                     <span className="chip bg-secondary"><Clock className="w-3 h-3"/>Open</span>}
                    <div className="text-[10px] text-foreground/50 mt-1">{r.quotes.length} quotes</div>
                  </div>
                </div>

                {r.quotes.length > 0 && (
                  <div className="mt-5 grid md:grid-cols-2 gap-3">
                    {r.quotes.map(q => (
                      <div key={q.seller_id} className="rounded-xl border border-border p-4" data-testid={`quote-${q.seller_id.slice(0,6)}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{q.seller_company || q.seller_name}</div>
                            <div className="text-[10px] text-foreground/60">{q.seller_name}</div>
                          </div>
                          <div className="display text-xl">{money(q.unit_price)}</div>
                        </div>
                        <div className="mt-3 flex items-center gap-3 text-xs text-foreground/60">
                          <span>MOQ {q.moq}</span>
                          <span>· {q.lead_days}d lead</span>
                        </div>
                        <div className="text-[10px] text-foreground/50 mt-2 italic">{q.notes}</div>
                        {r.status !== "awarded" && (
                          <Button size="sm" onClick={()=>accept(r, q.seller_id)} data-testid={`accept-${q.seller_id.slice(0,6)}`} className="mt-3 w-full rounded-full bg-foreground text-background hover:bg-foreground/90">Accept quote</Button>
                        )}
                        {r.awarded_seller_id === q.seller_id && <div className="mt-3 chip bg-accent/30 border border-accent/40 w-full justify-center"><Check className="w-3 h-3"/>Awarded</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
